const CreditCardAddBaseController = require('../../wallet/creditcard/credit-card-add-base.component').CreditCardAddBaseController;

class CreditCardAddPaymentController extends CreditCardAddBaseController {
    constructor($element, $sce, $q, $timeout, settings, digitalPayPaymentInstrumentsService, digitalPayIframeEventHandler, digitalPayPaymentDistributionService, digitalPayPaymentErrorDisplayService, digitalPayErrorMessageGenerator, digitalPayFormValidator) {
        super($element, $sce, $timeout, settings, digitalPayPaymentInstrumentsService, digitalPayIframeEventHandler);
        this.$q = $q;
        this.$timeout = $timeout;
        this.settings = settings;
        this.paymentDistributionService = digitalPayPaymentDistributionService;
        this.digitalPayPaymentErrorDisplayService = digitalPayPaymentErrorDisplayService;
        this.digitalPayErrorMessageGenerator = digitalPayErrorMessageGenerator;
        this.digitalPayFormValidator = digitalPayFormValidator;
        this.AddCreditCardId = this.paymentDistributionService.AddCreditCardId;
    }

    $onInit() {
        super.$onInit();
        this.creditCardTypes = this.settings.digitalPayCreditCardNetworks;
        this.errorMessage = this.settings.digitalPayError.creditCardAddCaptureFormLoadFailure;
        this.resetPromise();
        // Execute the promise (get instrument id) before payment, disable place order button, set credit card to take rest of payment
        this.prePaymentActionDefinitions.addAction(this.AddCreditCardId, this.submitCreditCardIframe.bind(this));
        this.paymentStateDefinitions.addDisablePaymentState(this.AddCreditCardId, this.settings.digitalPayScreenReaderMessages.creditCardAddDisableMessage);
        this.paymentDistributionService.addPaymentInFull(this.AddCreditCardId);
        this.isImpersonated = false;
        this.isBlank = true;
        this.payFrameIsValid = false;
    }

    /**
     * Implies that user no longer wants to make a payment using a new credit card, remove all related hooks
     */
    $onDestroy() {
        this.prePaymentActionDefinitions.removeAction(this.AddCreditCardId);
        this.paymentStateDefinitions.removeDisablePaymentState(this.AddCreditCardId);
        this.paymentDistributionService.removePayment(this.AddCreditCardId);
        this.resetErrorDisplayTimeouts();
        // Clear the error message when add card is closed
        this.onAddCardError({ });
        super.$onDestroy();
    }

    getPayingAmount() {
        const amount = this.paymentDistributionService.getPayment(this.AddCreditCardId);

        // Allow payment if the credit card add form is blank and it's $0 or if iframe is valid
        if ((this.isBlank && amount === 0) || this.payFrameIsValid) {
            this.paymentStateDefinitions.removeDisablePaymentState(this.AddCreditCardId);
        } else {
            this.paymentStateDefinitions.addDisablePaymentState(this.AddCreditCardId, this.settings.digitalPayScreenReaderMessages.creditCardAddDisableMessage);
        }

        return amount;
    }

    payFrameError(response) {
        if (this.isBlank) {
            this.submitIframeDeferred.resolve();
        }

        super.payFrameError(response);

        let globalError = '';
        let contextualError = '';
        // If form has expired, show ONLY a contextual error. Otherwise, show ONLY a global error.
        if (response.code === this.settings.digitalPayErrorCodes.creditCardCaptureFormExpired) {
            contextualError = this.settings.digitalPayError.creditCardAddCaptureFormExpiryFailure;
        } else {
            globalError = this.digitalPayErrorMessageGenerator.getNewCreditCardPaymentError(this.isSavingCard);
        }

        this.submitIframeDeferred.reject({ errorMessage: globalError });
        this.resetCardCapture();
        this.invokeAddCardError(contextualError);
    }

    payFrameSuccess(response) {
        super.payFrameSuccess(response);
        this.submitIframeDeferred.resolve({
            paymentInstrumentId: response.paymentInstrumentId,
            amount: this.getPayingAmount(),
            stepUpToken: response.stepUpToken,
        });

        this.$timeout(() => {
            if (this.hasPrePaymentActionFailures) {
                this.digitalPayPaymentInstrumentsService.deleteDigitalPayInstrument({ paymentInstrumentId: response.paymentInstrumentId });
                this.resetCardCapture();
            }
        });

        this.newCardPaymentInstrumentId = response.paymentInstrumentId;
    }

    payFrameValid() {
        super.payFrameValid();
        this.payFrameIsValid = true;
        this.$timeout(() => {
            this.paymentStateDefinitions.removeDisablePaymentState(this.AddCreditCardId);
        });
        this.clearValidationState();
    }

    payFrameInvalid() {
        super.payFrameInvalid();
        this.payFrameIsValid = false;
        this.$timeout(() => {
            this.paymentStateDefinitions.addDisablePaymentState(this.AddCreditCardId, this.settings.digitalPayScreenReaderMessages.creditCardAddDisableMessage);
        });
    }

    payFrameFocusGain(response) {
        if (this.focusLostObject === undefined) {
            return;
        }
        this.digitalPayPaymentErrorDisplayService.clearGlobalError();

        const errorMessage = this.digitalPayFormValidator.getAddNewCreditCardValidationError(this.focusLostObject, response.field);
        this.focusLostInvalidFieldsTimeoutPromise = this.invokeAddCardError(errorMessage);

        // Stop the missing fields message from displaying if focus on form is gained
        this.$timeout.cancel(this.focusLostBlankFieldsTimeoutPromise);
        this.focusLostBlankFieldsTimeoutPromise = undefined;
    }

    payFrameFocusLost(response) {
        if (response.allFieldsEmpty) {
            this.isBlank = true;
            this.clearValidationState();
            return;
        }

        this.isBlank = false;
        this.focusLostObject = response;

        const errorMessage = this.digitalPayFormValidator.getAddNewCreditCardValidationError(this.focusLostObject);
        this.focusLostInvalidFieldsTimeoutPromise = this.invokeAddCardError(errorMessage);

        // If the user moves away from the form, we display a missing fields message instead.
        // This is cancelled if the user focuses on the form immediately after moving away (e.g. tab from one field to another)
        if (this.hasBlankFields(this.focusLostObject)) {
            this.focusLostBlankFieldsTimeoutPromise = this.invokeAddCardError(this.settings.digitalPayError.creditCardFieldsMissingErrorsMessage);
        }
    }

    /**
     * @override
     */
    invokeAddCardError(errorMessage) {
        // Timeout exists here because we want to delay the error message appearing as a workaround for the UI design
        return this.$timeout(() => {
            this.onAddCardError({ errorMessage });
        }, this.settings.digitalPay.formFocusOutTimeoutMilliseconds);
    }

    hasBlankFields(response) {
        return (response.cardValid === null || response.expiryMonthValid === null || response.expiryYearValid === null || response.cvvValid === null);
    }

    clearValidationState() {
        this.focusLostObject = undefined;
        this.invokeAddCardError('');
    }

    submitCreditCardIframe() {
        this.submitIframe(); // Call from base
        return this.submitIframeDeferred.promise;
    }

    resetPromise() {
        // Reset promise (for next time)
        this.submitIframeDeferred = this.$q.defer();
    }

    resetCardCapture() {
        // Reset promise (for next time)
        this.resetPromise();
        this.clearIframeContents();
        this.setupIframe();
    }

    clearForm() {
        this.resetErrorDisplayTimeouts();
        this.clearIframeContents();
    }

    clearIframeContents() {
        super.clearIframeContents();
        this.isBlank = true;
    }

    resetErrorDisplayTimeouts() {
        this.$timeout.cancel(this.focusLostBlankFieldsTimeoutPromise);
        this.$timeout.cancel(this.focusLostInvalidFieldsTimeoutPromise);
        this.focusLostBlankFieldsTimeoutPromise = undefined;
        this.focusLostInvalidFieldsTimeoutPromise = undefined;
    }

    $onChanges(changes) {
        if (changes.shouldRemoveNewlyAddedInstruments && changes.shouldRemoveNewlyAddedInstruments.currentValue) {
            if (this.isSavingCard) {
                this.digitalPayPaymentInstrumentsService.deleteDigitalPayInstrument({ paymentInstrumentId: this.newCardPaymentInstrumentId });
            }

            if (changes.hasPrePaymentValidationFailure && changes.hasPrePaymentValidationFailure.currentValue) {
                const errorMessage = this.digitalPayErrorMessageGenerator.getNewCreditCardPaymentError(this.isSavingCard);
                this.digitalPayPaymentErrorDisplayService.pushToPaymentErrorBreakdown(errorMessage);
            }

            this.resetCardCapture();
        }
    }
}

CreditCardAddPaymentController.$inject = ['$element', '$sce', '$q', '$timeout', 'Settings', 'DigitalPayPaymentInstrumentsService', 'DigitalPayIframeEventHandler', 'DigitalPayPaymentDistributionService', 'DigitalPayPaymentErrorDisplayService', 'DigitalPayErrorMessageGenerator', 'DigitalPayFormValidator'];

const creditCardAddPaymentComponentDefinition = {
    controller: CreditCardAddPaymentController,
    template: require('./credit-card-add-payment.component.html').trim(),
    bindings: {
        allowedNetworks: '<',
        hasPrePaymentActionFailures: '<',
        isImpersonated: '<',
        isSavingCard: '<',
        onAddCardError: '&',
        paymentStateDefinitions: '<',
        prePaymentActionDefinitions: '<',
        shouldRemoveNewlyAddedInstruments: '<',
        shouldShowPayingAmount: '<',
        shouldShowSave: '<',
    },
};

module.exports = {
    CreditCardAddPaymentController,
    creditCardAddPaymentComponentDefinition,
};



// WEBPACK FOOTER //
// ./src/digitalpay/creditcard/credit-card-add-payment.component.js